import { createHOC } from 'vue-hoc';
import omit from 'lodash/omit';
import clone from 'lodash/clone';
import get from 'lodash/get';
import Promise from 'bluebird';

import withUser from '~lib/common/hoc/withUser';
import auth from '~lib/common/providers/auth';
import userManagement from '~lib/common/providers/userManagement';
import parseGroupMetadata from '~lib/common/utils/parseGroupMetadata';

import GroupDetails from './GroupDetails';

const PAGE_SIZE = 50;

const options = {
  name: 'GroupDetailsContainer',
  props: {
    id: {
      type: String,
    },
    user: {
      type: Object,
    },
  },
  data() {
    return {
      state: {
        loading: false,
        updating: false,
        removing: false,
        inviting: false,
        savingUser: false,
        searchingGroups: false,
        group: null,
        users: [],
        groupSearchResults: [],
        userSearchResults: [],
        allUsers: [],
        error: null,
        updateError: null,
        inviteError: null,
        saveUserError: null,
        invites: [],
        offset: 0,
      },
    };
  },
  async created() {
    if (!(await auth.isAuthenticated())) {
      this.$router.replace({ name: 'login' });
      return;
    }
    await this.fetchData();
    this.fetchPage().then(() => {
      this.trackUserCount();
    });
  },
  watch: {
    '$route.meta.archived'() {
      this.state.users = [];
      this.state.offset = 0;
      this.fetchData().then(() => {
        this.fetchPage();
      });
    },
  },
  computed: {
    showArchived() {
      return !!this.$route.meta.archived;
    },
    invitees() {
      return this.state.invites
        .filter((invite) => invite.user_id !== this.state.group.metadata.ownerId)
        .map((invite) => ({
          email: invite.email,
          user_id: invite.user_id,
          pending: true,
          declined: invite.declined,
        }));
    },
  },
  methods: {
    async fetchData() {
      try {
        this.state.loading = true;
        const groupId = this.id;
        if (groupId) {
          const [
            groupDetailsResponse,
            inviteResponse,
            keyOwnerResponse
          ] = await Promise.all([
            userManagement.get(`/extension/groups/${groupId}`),
            userManagement.get(`invites/org/${groupId}`),
            userManagement.getKeyOwner(groupId),
          ]);
          this.state.group = {
            ...groupDetailsResponse.data,
            metadata: {
              ...parseGroupMetadata(groupDetailsResponse.data),
            },
          };
          this.state.keyOwner = keyOwnerResponse;
          this.state.invites = inviteResponse.data.invites;
        }
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        // leave this.state.loading = true, will be set to false by fetchPage
      }
    },
    async fetchPage() {
      try {
        this.state.loading = true;
        const groupId = this.id;
        if (groupId) {
          const numMembers = this.state.group.members.length;
          const numInvites = this.invitees.length;

          // If in the range of users, fetch users
          let users = [];
          if (this.state.offset < numMembers) {
            users = await userManagement.getGroupMembers(groupId, this.showArchived, undefined, this.state.offset, PAGE_SIZE);
          }

          // If in the range of invites, concatenate invitees
          let invitees = [];
          if (this.state.offset + PAGE_SIZE > numMembers) {
            const inviteOffset = Math.max(0, this.state.offset - numMembers);
            let chunkSize;
            if (this.state.offset > numMembers) {
              chunkSize = PAGE_SIZE;
            } else {
              chunkSize = Math.max(0, PAGE_SIZE - (numMembers - this.state.offset));
            }
            invitees = this.invitees.slice(inviteOffset, Math.min(numInvites, inviteOffset + chunkSize));
          }

          this.state.users = users.concat(invitees);
        }
        this.state.allUsers = clone(this.state.users);
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
    userRole(user) {
      return get(user, `app_metadata.orgSpecificRoles.${this.state.group._id}`) || get(user, 'app_metadata.role');
    },
    trackUserCount() {
      const nonPendingVisibleUsers = this.state.users.filter(user => !user.pending);
      if (this.state.group && !this.state.group.metadata.parent) {
        this.$analytics.event('Org Management', this.showArchived ? 'archived_org_users_loaded' : 'org_users_loaded', this.state.group.name, undefined, {
          user_count: nonPendingVisibleUsers.length,
          admin_count: nonPendingVisibleUsers.filter(user => this.userRole(user) === 'ADMIN').length,
          instructor_count: nonPendingVisibleUsers.filter(user => this.userRole(user) === 'INSTRUCTOR').length,
          student_count: nonPendingVisibleUsers.filter(user => (!this.userRole(user) || this.userRole(user) === 'STUDENT')).length,
        })
      }
    },
    async update(updates) {
      this.state.updateError = null;
      try {
        this.state.updating = true;
        const updateGroupResponse = await userManagement.patch(
          `/extension/groups/${this.state.group._id}`,
          updates
        );
        const updatedGroup = updateGroupResponse.data;
        const keyOwnerChanged = !!get(updates, 'metadata.ownerId');
        const productTypeChanged = !!get(updates, 'metadata.productType');
        if (keyOwnerChanged || productTypeChanged) {
          await auth.renewSession();
        }
        this.state.group = updatedGroup;
      } catch (e) {
        console.error(e);
        this.state.updateError = e;
      } finally {
        this.state.updating = false;
      }
    },
  },
};

const renderWith = {
  props: {
    loading() {
      return this.state.loading;
    },
    updating() {
      return this.state.updating;
    },
    removing() {
      return this.state.removing;
    },
    inviting() {
      return this.state.inviting;
    },
    savingUser() {
      return this.state.savingUser;
    },
    searchingGroups() {
      return this.state.searchingGroups;
    },
    group() {
      return this.state.group;
    },
    users() {
      return this.state.users;
    },
    userSearchResults() {
      return this.state.userSearchResults;
    },
    groupSearchResults() {
      return this.state.groupSearchResults;
    },
    error() {
      return this.state.error;
    },
    updateError() {
      return this.state.updateError;
    },
    inviteError() {
      return this.state.inviteError;
    },
    saveUserError() {
      return this.state.saveUserError;
    },
    displayArchived() {
      return this.showArchived;
    },
    paginationText() {
      if (this.state.group && this.state.group.members) {
        const numMembers = this.state.group.members.length;
        const numInvites = this.invitees.length;
        const numPages = Math.ceil((numMembers + numInvites) / PAGE_SIZE);
        const currentPage = Math.floor(this.state.offset / PAGE_SIZE) + 1;
        return `${currentPage} of ${numPages}`;
      }
      return '';
    },
    keyOwner() {
      return this.state.keyOwner;
    }
  },
  listeners: {
    async update(updates) {
      this.update(updates);
    },
    async removeUsers(users, groupId = this.id) {
      const pendingUsersToRemove = users.filter((u) => u.pending);
      this.state.error = null;
      const userIds = users
        .filter((u) => !u.pending)
        .map((user) => user.user_id);
      try {
        this.state.removing = true;
        if (groupId) {
          if (pendingUsersToRemove.length) {
            await Promise.all(
              pendingUsersToRemove.map((user) => {
                userManagement.delete(
                  `invites/org/${groupId}?email=${encodeURIComponent(
                    user.email
                  )}`
                );
              })
            );
          }
          if (userIds.length) {
            await userManagement.delete(
              `/extension/groups/${groupId}/members`,
              {},
              { data: userIds }
            );
          }
        } else {
          await Promise.map(users, async (user) => {
            // Before removing a user globally, remove him
            // remove him from all his groups first to prevent
            // ghost members
            if (user.groups) {
              await Promise.map(
                user.groups,
                (group) =>
                  userManagement.delete(
                    `/extension/groups/${group._id}/members`,
                    {},
                    { data: [user.user_id] }
                  ),
                // Authorization extension does not support
                // modifying a group concurrently with two requests
                { concurrency: 1 }
              );
            }
            userManagement.delete(`/users/${user.user_id}`);
          });
        }
        this.state.users = this.state.users.filter(
          (u) => !userIds.includes(u.user_id)
        );
        this.state.invites = this.state.invites.filter(
          (inv) =>
            !pendingUsersToRemove.some((user) => user.user_id === inv.user_id)
        );
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.removing = false;
        if (this.state.group) {
          this.state.group.metadata.memberCount =
            this.state.group.metadata.memberCount - 1;
        }
      }
    },
    async filterUsers(term) {
      if (!term) {
        this.state.users = this.state.allUsers;
        return;
      }
      try {
        this.state.error = null;
        this.state.loading = true;
        const groupId = this.id;
        if (this.showArchived) {
          const archivedUsersResponse = await userManagement.get(
            '/archived-users',
            {
              filter: {
                _search: {
                  $regex: term,
                  $options: 'i',
                },
              },
            }
          );
          const { archivedusers: users } = archivedUsersResponse.data;
          this.state.users = users;
        } else if (groupId) {
          const response = await userManagement.get('/search/users', {
            term,
            orgId: groupId,
          });
          const { users } = response.data;
          this.state.users = users;
        } else {
          const response = await userManagement.get('/users', {
            // Auth0 wildcard searches must use a term with at least 3 characters
            q: `email:*${term}* OR given_name:*${term}* OR family_name:*${term}*`,
          });
          const users = response.data;
          // Populate user orgs when we are in the list of all users
          await Promise.all(
            users.map(async (user) => {
              const userGroupsResponse = await userManagement.get(
                `/extension/users/${user.user_id}/groups`
              );
              const userGroups = userGroupsResponse.data;
              const orgGroup = userGroups.find(
                (group) => !parseGroupMetadata(group).parent
              );
              user.org = orgGroup;
              user.groups = userGroups;
            })
          );
          this.state.users = users;
        }
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
    async searchUsers(term) {
      if (!term) {
        this.state.userSearchResults = [];
        return;
      }
      try {
        this.state.loading = true;
        const group = this.state.group;
        // Is we are in a subgroup, we want to search org wide
        const searchGroupId = group.metadata.parent
          ? group.metadata.parent
          : group.id;
        const response = await userManagement.get('/search/users', {
          term,
          orgId: searchGroupId,
        });
        const { users } = response.data;
        this.state.userSearchResults = users;
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
    async searchGroups(term) {
      if (!term) {
        return;
      }
      try {
        this.state.searchingGroups = true;
        const response = await userManagement.get('/search/groups', {
          term,
        });
        const { groups } = response.data;
        this.state.groupSearchResults = groups;
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.searchingGroups = false;
      }
    },
    async invite(email, org, role = 'STUDENT') {
      try {
        this.state.inviteError = null;
        this.state.inviting = true;
        const response = await userManagement.post('/invitations/invite', {
          users: [
            {
              email,
              role,
            },
          ],
          org: {
            id: org._id,
          },
        });
        userManagement.get(`invites/org/${this.id}`).then(response => {
          this.state.invites = response.data.invites;
        })
      } catch (e) {
        console.error(e);
        this.state.inviteError = e;
      } finally {
        this.state.inviting = false;
      }
    },
    async addUser(user) {
      try {
        this.state.inviteError = null;
        this.state.inviting = true;
        const groupId = this.state.group._id;
        await userManagement.patch(`/extension/groups/${groupId}/members`, [
          user.user_id,
        ]);
        this.state.users.unshift(user);
      } catch (e) {
        console.error(e);
        this.state.inviteError = e;
      } finally {
        this.state.inviting = false;
      }
    },
    async saveUser(user, newOrg, oldOrg) {
      this.state.saveUserError = null;
      this.state.savingUser = true;
      try {
        this.state.savingUser = true;
        const response = await userManagement.patch(
          `/users/${user.user_id}?orgId=${this.state.group._id}`,
          omit(user, 'user_id')
        );
        if (newOrg) {
          await userManagement.patch(
            `/extension/groups/${newOrg._id}/members`,
            [user.user_id]
          );
          if (oldOrg._id) {
            await userManagement.delete(
              `/extension/groups/${oldOrg._id}/members`,
              {},
              { data: [user.user_id] }
            );
          }
        }
        const updatedUser = response.data;
        if (this.id && newOrg) {
          // Remove the user from the list if org of user changed
          this.state.users = this.state.users.filter(
            (u) => u.user_id !== user.user_id
          );
        } else {
          this.state.users = this.state.users.map((u) =>
            u.user_id === user.user_id ? { ...updatedUser, org: newOrg } : u
          );
        }
      } catch (e) {
        this.state.saveUserError = e;
      } finally {
        this.state.savingUser = false;
      }
    },
    async resetPassword(email) {
      this.state.savingUserError = null;
      this.state.savingUser = true;
      try {
        await auth.sendPasswordReset(email);
      } catch (error) {
        this.state.savingUserError = { ...error, message: error.description };
      } finally {
        this.state.savingUser = false;
      }
    },
    bulkInviteSubmitted(newUsers, org) {
      userManagement.get(`invites/org/${this.id}`).then(response => {
        this.state.invites = response.data.invites;
      })
      userManagement.get(`invites/org/${this.id}`).then(response => {
        this.state.invites = response.data.invites;
      })
    },
    firstPage() {
      if (!this.state.group || !this.state.group.members) {
        return;
      }
      this.state.offset = 0;
      this.fetchPage();
    },
    previousPage() {
      if (!this.state.group || !this.state.group.members) {
        return;
      }
      if (this.state.offset - PAGE_SIZE < 0) {
        return;
      }
      this.state.offset -= PAGE_SIZE;
      this.fetchPage();
    },
    nextPage() {
      if (!this.state.group || !this.state.group.members) {
        return;
      }
      const numMembers = this.state.group.members.length;
      const numInvites = this.invitees.length;
      if (this.state.offset + PAGE_SIZE >= (numMembers + numInvites)) {
        return;
      }
      this.state.offset += PAGE_SIZE;
      this.fetchPage();
    },
    lastPage() {
      if (!this.state.group || !this.state.group.members) {
        return;
      }
      const numMembers = this.state.group.members.length;
      const numInvites = this.invitees.length;
      this.state.offset = Math.floor((numMembers + numInvites) / PAGE_SIZE) * PAGE_SIZE;
      this.fetchPage();
    },
  },
};

const GroupDetailsContainer = createHOC(GroupDetails, options, renderWith);
export default withUser()(GroupDetailsContainer);
